import Image from 'next/image';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../core/Tracking/Amplitude/Amplitude';
import { IconButton } from '../../../../design-system/components/form/button/IconButton';
import { IconImage } from '../../../../design-system/components/icons/Image';
import { IllustrationQuotationMark } from '../../../../design-system/components/illustration/QuotationMark';
import { RATIO } from '../../../../design-system/components/ratio/Ratio';
import { Typography } from '../../../../design-system/components/typography/Typography';
import { useBreakPoint } from '../../../../domains/Layout/BreakPoint/BreakPoint';
import {
    imageLoaderForId,
    imageLoaderForPicture,
} from '../../../../domains/Layout/Image/URL/ImageURL';
import { Destination } from '../../../../resources/destination/domain/types/destination';
import { UgcHighlight } from '../../../../resources/ugcHighlight/domain/types/ugcHighlight';
import { convertDateToUTC } from '../../../utilities/date';
import { PickDomain } from '../../../utilities/types/PickDomain';
import { UGCHighlightModal } from '../UGCHighlightModal/UGCHighlightModal';

import style from './UGCHighlightCard.module.css';

type UGCHighlightCardProps = {
    ugcHighlight: PickDomain<
        UgcHighlight,
        'id' | 'username' | 'segment' | 'validatedAt' | 'description' | 'pictures'
    >;
    index: number;
    destination: PickDomain<Destination, 'id' | 'slug'>;
};

export function UGCHighlightCard({ ugcHighlight, index, destination }: UGCHighlightCardProps) {
    const { formatDate, formatMessage } = useIntl();
    const { isXs, isSm, isMd } = useBreakPoint();
    const { trackEvent } = useTrackEventAmplitude();

    const [isModalOpen, setIsModalOpen] = useState(false);

    const formatSafeDate = (dateString: string) => {
        if (!dateString) return null;

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return null;

        return formatDate(convertDateToUTC(date), {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        });
    };

    const date = formatSafeDate(ugcHighlight.validatedAt);

    const isSmallScreen = isXs() || isSm() || isMd();
    const imageRatio = isSmallScreen ? RATIO['16/9'] : RATIO['5/8'];

    const handleImageClick = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.ugcClicked({
                ...defaultProperties,
                destination_id: destination.id,
                destination_slug: destination.slug,
                block: 'ugc',
                card_number: index + 1,
                is_clickable: ugcHighlight.pictures.length > 0,
            });
        });

        setIsModalOpen(true);
    };

    return (
        <div className={style.card}>
            <div className={style.contentWrapper}>
                <div className={style.contentHeader}>
                    <Typography scale="primary-s-extra-bold">{ugcHighlight.username}</Typography>
                    <Typography scale="primary-xxs-regular" className={style.cardSubtitle}>
                        {formatMessage({ id: 'global.ugc.card.subtitle' })}
                    </Typography>
                </div>

                <div className={style.descriptionWrapper}>
                    <IllustrationQuotationMark
                        color="var(--color-neutral-900)"
                        className={style.quotationMark}
                        opacity="0.3"
                    />
                    <Typography scale="primary-xxs-regular" className={style.description}>
                        {ugcHighlight.description}
                    </Typography>
                </div>

                <div className={style.cardFooter}>
                    <Typography className={style.cardTag} scale="primary-xs-bold">
                        {formatMessage({
                            id: `global.ugc.segment.${ugcHighlight.segment}.label`,
                        })}
                    </Typography>

                    {date ? (
                        <Typography scale="primary-xxs-regular" className={style.cardDate}>
                            {date}
                        </Typography>
                    ) : null}
                </div>
            </div>

            {ugcHighlight.pictures.length > 0 ? (
                <div className={style.imageWrapper} onClick={handleImageClick}>
                    {ugcHighlight.pictures.slice(0, 1).map((picture) => (
                        <Image
                            className={style.image}
                            key={picture.id}
                            src={picture.id}
                            alt={picture.legend}
                            fill
                            sizes="(min-width: 1200px) 600px, (min-width: 992px) 500px, 300px"
                            loader={imageLoaderForPicture(picture, {
                                aspectRatio: imageRatio,
                            })}
                            loading="lazy"
                        />
                    ))}
                    <div>
                        <IconButton
                            className={style.seeImagesButton}
                            Icon={IconImage}
                            onClick={handleImageClick}
                        />
                    </div>
                </div>
            ) : (
                <div
                    className={style.imagePlaceholderWrapper}
                    onClick={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.ugcClicked({
                                ...defaultProperties,
                                destination_id: destination.id,
                                destination_slug: destination.slug,
                                block: 'ugc',
                                card_number: index + 1,
                                is_clickable: false,
                            });
                        });
                    }}
                >
                    <Image
                        className={style.imagePlaceholder}
                        src="1824398"
                        alt=""
                        fill
                        sizes="(min-width: 1200px) 600px, (min-width: 992px) 500px, 300px"
                        loader={imageLoaderForId({
                            aspectRatio: imageRatio,
                        })}
                        loading="lazy"
                    />
                </div>
            )}

            {isModalOpen ? (
                <UGCHighlightModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    ugcHighlight={ugcHighlight}
                    segment={ugcHighlight.segment}
                />
            ) : null}
        </div>
    );
}
